import { CarIcon, CarProfileIcon } from '@phosphor-icons/react';
import { fmtTime, fmtWeekdayDate } from '../../../flight/results/flight_format.js';
import { useCheckoutEnv } from '../../drawer/env_context.js';
import type { CheckoutMobilityCopy } from '../../model/checkout_copy.js';
import type { MobilityItemSource } from '../../model/checkout_source.js';
import './item_detail.css';

type MobilityItemDetail = MobilityItemSource['detail'];

export interface MobilityItemDetailProps {
  detail: MobilityItemDetail;
  price: { amount: number; currency: string };
}

type MobilityLegDetail = MobilityItemDetail['outward'];

/** Cabeçalho do resumo: ícone por serviço + fornecedor + rótulo da seção
 *  (corrida/carro com motorista) — mesma seção de `mobility_offers`. */
function MobilityHead({ detail }: { readonly detail: MobilityItemDetail }) {
  const copy = useCheckoutEnv().copy.mobility;
  const Icon = detail.service === 'cooperative' ? CarProfileIcon : CarIcon;
  const section = detail.service === 'cooperative' ? copy.sectionRide : copy.sectionCarDriver;
  return (
    <div className="pcc-mob__head">
      <Icon size={16} />
      <span className="pcc-mob__supplier">{detail.supplierName}</span>
      <span className="pcc-mob__section">· {section}</span>
    </div>
  );
}

/** Um trecho (ida OU volta): data/horário + origem → destino e, embaixo, as
 *  paradas na ordem do trajeto. Sem destino (carro com motorista, que fica à
 *  disposição) a rota é só o embarque. Núcleo compartilhado pelo compacto e
 *  pelo expandido, espelhando `BusTrip`. */
function MobilityLeg({ leg }: { readonly leg: MobilityLegDetail }) {
  const { copy, locale } = useCheckoutEnv();
  const stops = leg.stops ?? [];
  return (
    <div className="pcc-mob__leg">
      <div className="pcc-mob__when">
        <span className="pcc-mob__date">{fmtWeekdayDate(leg.scheduleDate, locale)}</span>
        <span className="pcc-mob__time">{fmtTime(leg.scheduleDate)}</span>
      </div>
      <div className="pcc-mob__route">
        <span className="pcc-mob__place">{leg.from}</span>
        {leg.to ? (
          <>
            <span className="pcc-mob__arrow" aria-hidden="true">
              →
            </span>
            <span className="pcc-mob__place">{leg.to}</span>
          </>
        ) : null}
      </div>
      {stops.length > 0 ? (
        <p className="m-0 text-caption text-muted-foreground">{copy.mobility.stops(stops)}</p>
      ) : null}
    </div>
  );
}

/** Rótulo do pacote diário do carro c/ motorista ("Sedan executivo · 3 diárias ·
 *  12h/dia · 150 km", ou "km livre" no lugar dos km), reaproveitando as chaves
 *  de `mobility_offers` (DRY — mesmo domínio, mesma franquia de horas/dia e
 *  diárias). Vazio para a cooperativa (preço fechado, sem pacote). */
export function packageLabel(detail: MobilityItemDetail, copy: CheckoutMobilityCopy): string {
  const parts: string[] = [];
  if (detail.vehicle) parts.push(detail.vehicle);
  if (detail.days) parts.push(copy.days(detail.days));
  if (detail.hours) parts.push(copy.hoursPerDay(detail.hours));
  if (detail.freeKm) parts.push(copy.freeKm);
  else if (detail.km != null) parts.push(copy.distanceKm(detail.km));
  return parts.join(' · ');
}

/** O voo que a corrida acompanha (o texto que a cooperativa lê, sempre em pt-BR)
 *  e o aviso de que o valor previsto não inclui as paradas — a busca de
 *  cooperativas cota só a rota direta. */
export function MobilityNotes({ detail }: { readonly detail: MobilityItemDetail }) {
  const copy = useCheckoutEnv().copy.mobility;
  return (
    <>
      {detail.flightNote ? (
        <p className="m-0 text-caption text-muted-foreground">{detail.flightNote}</p>
      ) : null}
      {detail.excludesStops ? (
        <p className="m-0 text-caption text-muted-foreground">{copy.excludesStops}</p>
      ) : null}
    </>
  );
}

/** Expanded variant — mobility summary inside the unified checkout DRAWER
 *  ("Sua reserva" panel). The compact head/leg + the return leg (cooperative
 *  round trip, with its own stops) + the flight note and the stops notice + the
 *  car-driver extras worth surfacing (bilingual driver, overnight stays) —
 *  mirrors how bus reuses the compact trip plus one extra row for its seat
 *  class. */
export function MobilityItemDetailExpanded({ detail, price }: Readonly<MobilityItemDetailProps>) {
  const copy = useCheckoutEnv().copy.mobility;
  const pkg = packageLabel(detail, copy);
  return (
    <div className="pcc-mob">
      <div className="pcc-mob__main">
        <MobilityHead detail={detail} />
        <MobilityLeg leg={detail.outward} />
        {detail.return && <MobilityLeg leg={detail.return} />}
        {pkg && <p className="pcc-mob__package">{pkg}</p>}
        <MobilityNotes detail={detail} />
        <span className="pcc-mob__sr-price">
          {price.amount} {price.currency}
        </span>
      </div>
      {(detail.bilingual === true || (detail.overnightDays ?? 0) > 0) && (
        <div className="pcc-mob__extras">
          {detail.bilingual && <span className="pcc-mob__extra">{copy.bilingual}</span>}
          {detail.overnightDays ? (
            <span className="pcc-mob__extra">{copy.overnight(detail.overnightDays)}</span>
          ) : null}
        </div>
      )}
    </div>
  );
}
